<template>
  <div class="client-scoring">
    <!-- Skeleton -->
    <div v-if="loading" class="cs-skeleton">
      <div v-for="n in 5" :key="n" class="cs-skeleton-row">
        <div class="skeleton-line w40"></div>
        <div class="skeleton-bar"></div>
        <div class="skeleton-line w20"></div>
      </div>
    </div>

    <!-- List -->
    <div v-else-if="clients.length" class="cs-list">
      <div
        v-for="client in clients"
        :key="client.id"
        class="cs-item"
        @click="onItemClick(client, $event)"
      >
        <div class="cs-row-top">
          <span class="cs-name">{{ client.name }}</span>
          <div class="cs-row-right">
            <span :class="['cs-status-label', client.status]">
              {{ statusLabel(client.status) }}
            </span>
            <span class="cs-score">{{ client.conversion_score }}%</span>
          </div>
        </div>
        <div class="cs-bar-row">
          <div class="cs-bar-track">
            <div
              class="cs-bar"
              :style="{
                width: client.conversion_score + '%',
                background: statusColor(client.status)
              }"
            ></div>
          </div>
          <div class="cs-actions" @click.stop>
            <el-button
              size="mini"
              type="text"
              class="cs-action-btn"
              @click="openMessage(client)"
            >
              <i class="el-icon-chat-dot-round"></i>
            </el-button>
            <el-dropdown
              size="mini"
              trigger="click"
              @command="cmd => changeStatus(client, cmd)"
            >
              <el-button size="mini" type="text" class="cs-action-btn">
                <i class="el-icon-more"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="hot">🔥 Caliente</el-dropdown-item>
                <el-dropdown-item command="warm">🌊 Tibio</el-dropdown-item>
                <el-dropdown-item command="cold">❄️ Frío</el-dropdown-item>
                <el-dropdown-item command="dormant"
                  >💤 Dormido</el-dropdown-item
                >
                <el-dropdown-item command="new">🌱 Nuevo</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty -->
    <div v-else class="cs-empty">
      <i class="el-icon-data-analysis"></i>
      <span>Sin datos de scoring</span>
    </div>
  </div>
</template>

<script>
import dashboardService from "../services/dashboardService";

const STATUS_COLORS = {
  hot: "#1d9e75",
  warm: "#3fdbbf",
  cold: "#ba7517",
  dormant: "#e24b4a",
  new: "#534ab7"
};

const STATUS_LABELS = {
  hot: "Caliente",
  warm: "Tibio",
  cold: "Frio",
  dormant: "Dormido",
  new: "Nuevo"
};

export default {
  name: "ClientScoring",
  emits: ["open-client"],
  data() {
    return {
      loading: true,
      clients: []
    };
  },
  created() {
    this.fetchScoring();
  },
  methods: {
    async fetchScoring() {
      this.loading = true;
      try {
        const res = await dashboardService.getClientScoring({
          params: { limit: 5 }
        });
        if (res.data && res.data.success) {
          this.clients = res.data.data.clients || res.data.data || [];
        }
      } catch (e) {
        // Mantener valores actuales
      } finally {
        this.loading = false;
      }
    },
    statusColor(status) {
      return STATUS_COLORS[status] || "#909399";
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status;
    },
    onItemClick(client, event) {
      // No emitir si el click fue en los botones de acción
      if (event.target.closest(".cs-actions")) return;
      this.$emit("open-client", client);
    },
    openMessage(client) {
      this.$root.$emit("open-bulk-message", {
        recipients: [
          {
            id: client.client_id || client.id,
            name: client.client_name || client.name,
            phone: client.phone || null,
            email: client.email || null
          }
        ],
        context: "clients"
      });
    },
    changeStatus(client, newStatus) {
      const idx = this.clients.findIndex(c => c.id === client.id);
      if (idx !== -1) {
        this.$set(this.clients, idx, {
          ...this.clients[idx],
          status: newStatus
        });
      }
      this.$message.success(
        `Estado de ${client.name} cambiado a ${STATUS_LABELS[newStatus] ||
          newStatus}`
      );
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$accent: #3fdbbf;
$border: #e4eaf6;
$bg: #f7f8f9;

.client-scoring {
  // No background/border — lo da el .bi-card padre
}
.cs-list {
  display: flex;
  flex-direction: column;
}
.cs-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  cursor: pointer;
  padding: 8px 0;
  border-bottom: 1px solid $border;
  transition: background 0.15s;
  margin: 0 -18px;
  padding-left: 18px;
  padding-right: 18px;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: $bg;
    .cs-actions {
      opacity: 1;
    }
  }
}
.cs-row-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.cs-row-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.cs-name {
  font-size: 13px;
  font-weight: 600;
  color: $primary;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.cs-status-label {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 8px;
  flex-shrink: 0;
  white-space: nowrap;
  &.hot {
    background: #e4fff4;
    color: #1d9e75;
  }
  &.warm {
    background: #e0fcf4;
    color: #3fdbbf;
  }
  &.cold {
    background: #fff8e6;
    color: #ba7517;
  }
  &.dormant {
    background: #fff0f0;
    color: #e24b4a;
  }
  &.new {
    background: #ece8ff;
    color: #534ab7;
  }
}
.cs-bar-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.cs-bar-track {
  flex: 1;
  height: 6px;
  background: $bg;
  border-radius: 3px;
  overflow: hidden;
}
.cs-bar {
  height: 100%;
  border-radius: 3px;
  transition: width 0.6s ease;
  min-width: 2px;
}
.cs-score {
  font-size: 11px;
  font-weight: 700;
  color: $primary;
  width: 36px;
  text-align: right;
  flex-shrink: 0;
}
.cs-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.15s;
  flex-shrink: 0;
  margin-left: 4px;
}
.cs-action-btn {
  font-size: 12px;
  padding: 2px 4px;
  color: #909399;
  &:hover {
    color: $primary;
  }
}

.cs-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  color: #c0c4cc;
  gap: 8px;
  i {
    font-size: 28px;
  }
  span {
    font-size: 12px;
  }
}

// Skeleton
.cs-skeleton {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.cs-skeleton-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.skeleton-line {
  height: 10px;
  background: linear-gradient(90deg, #f0f2f7 25%, #e8ecf5 50%, #f0f2f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  &.w20 {
    width: 20%;
  }
  &.w40 {
    width: 40%;
  }
}
.skeleton-bar {
  flex: 1;
  height: 8px;
  background: linear-gradient(90deg, #f0f2f7 25%, #e8ecf5 50%, #f0f2f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
